import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchInstruments, fetchSessionToday, fetchGapStats, fetchORBStats,
  fetchSessionTypeStats, fetchTimingStats, fetchMultiDayStats, fetchSimilarDays, fetchLevelProbabilities, refreshSession, fetchFilteredStats, fetchAdr, fetchSessionContext,
  type Instrument, type SessionSnapshot, type StatItem, type TimingData, type SimilarDayData, type LevelProbData, type FilterState, type SessionContextData
} from './api';
import FilterPanel from './FilterPanel';
import ORBFilterPanel from './ORBFilterPanel';
import EdgeSummaryPanel from './EdgeSummaryPanel';
import SimilarDaysPanel from './SimilarDaysPanel';
import LevelProbabilitiesPanel from './LevelProbabilitiesPanel';
import TradingViewChart from './TradingViewChart';
import ORBDashboard from './ORBDashboard';
import PreMarketPanel from './PreMarketPanel';
import CurrenciesPanel from './CurrenciesPanel';
import BreakoutScanPanel from './BreakoutScanPanel';

// ---- Utility components ----

/** Returns true if the given instrument's market is currently open.
 * DAX: Mon-Fri 07:00-15:30 UTC
 * US indices (US30, NASDAQ, SP500): Mon-Fri 16:30-23:00 UTC */
function isMarketOpen(inst: string): boolean {
  const now = new Date();
  const day = now.getUTCDay(); // 0=Sun, 6=Sat
  if (day === 0 || day === 6) return false;
  const hour = now.getUTCHours();
  const min = now.getUTCMinutes();
  const totalMin = hour * 60 + min;
  if (inst === 'DAX') {
    // 07:00-15:30 UTC
    return totalMin >= 7 * 60 && totalMin < 15 * 60 + 30;
  }
  if (['US30', 'NASDAQ', 'SP500'].includes(inst)) {
    // 16:30-23:00 UTC
    return totalMin >= 14 * 60 + 30 && totalMin < 21 * 60;
  }
  return false;
}

function pctBar(value: number, max = 100, color = '#3b82f6') {
  const raw = (value / max) * 100;
  const w = isFinite(raw) && raw >= 0 ? Math.min(100, raw) : 0;
  return (
    <div style={styles.pctBarWrap}>
      <div style={styles.pctBarFill ? { ...styles.pctBarFill, width: `${w}%`, background: color } : { width: `${w}%`, background: color }} />
      <span style={styles.pctBarLabel}>{isFinite(value) ? value.toFixed(1) : '0.0'}%</span>
    </div>
  );
}

function card(title: string, children: React.ReactNode, color = '#2934ff') {
  return (
    <div style={{ ...styles.card, borderTop: `2px solid ${color}` }}>
      <div style={styles.cardTitle}>{title}</div>
      {children}
    </div>
  );
}

function loadingSpinner() {
  return <div style={styles.loading}>Loading data...</div>;
}

// ---- Sub-panels ----

function LiveSessionPanel(props: { snapshot: SessionSnapshot | null; similarDays: SimilarDayData | null; adrPoints?: number; gapFillPct?: number | null; onRefresh: () => void; instrument: string }) {
  const { snapshot, similarDays, adrPoints, gapFillPct, onRefresh, instrument } = props;
  if (!snapshot) return <div style={styles.loading}>No session data — run backfill first</div>;

  const gapSign = snapshot.gap_points >= 0 ? '+' : '';
  const gapColor = snapshot.gap_points >= 0 ? '#22c55e' : '#ef4444';

  // Determine gap status
  const gapDirLabel = snapshot.gap_points > 0 ? '↑ Gap Up' : snapshot.gap_points < 0 ? '↓ Gap Down' : '— No Gap';
  const gapDirColor = snapshot.gap_points > 0 ? '#22c55e' : snapshot.gap_points < 0 ? '#ef4444' : '#94a3b8';

  // Yesterday's range (from API — computed from previous trading day's candles)
  const yesterdayHigh = snapshot.yesterday_high ?? null;
  const yesterdayLow = snapshot.yesterday_low ?? null;

  // Open position relative to yesterday's range
  const openPosLabel = (() => {
    if (!snapshot.session_open || !yesterdayHigh || !yesterdayLow) return null;
    if (snapshot.session_open > yesterdayHigh) return { label: '↑ Above Prev Range', color: '#22c55e' };
    if (snapshot.session_open < yesterdayLow) return { label: '↓ Below Prev Range', color: '#ef4444' };
    return { label: '↔ Inside Prev Range', color: '#f59e0b' };
  })();

  // Levels touched hit rates from similarDays
  // DAX has no Globex session — prev daily = prev session (same level)
  const isDAX = instrument === 'DAX';
  const hitStats = similarDays ? [
    { label: 'Prev Sess High', pct: similarDays.hitPrevSessHighPct, value: snapshot.prev_high },
    { label: 'Prev Sess Low', pct: similarDays.hitPrevSessLowPct, value: snapshot.prev_low },
    { label: 'Prev Daily High', pct: isDAX ? similarDays.hitPrevSessHighPct : similarDays.hitPrevDailyHighPct, value: isDAX ? snapshot.prev_high : snapshot.yesterday_high },
    { label: 'Prev Daily Low', pct: isDAX ? similarDays.hitPrevSessLowPct : similarDays.hitPrevDailyLowPct, value: isDAX ? snapshot.prev_low : snapshot.yesterday_low },
  ] : [];

  const priceMovePct = adrPoints > 0 ? Math.abs((snapshot.current_price - snapshot.session_open) / adrPoints * 100) : null;
  const moveSign = snapshot.current_price >= snapshot.session_open ? '+' : '';
  const moveColor = priceMovePct !== null ? (snapshot.current_price >= snapshot.session_open ? '#22c55e' : '#ef4444') : '#94a3b8';
  // True ROC: % change from session open
  const rocPct = snapshot.session_open ? ((snapshot.current_price - snapshot.session_open) / snapshot.session_open * 100) : null;
  const rocSign = rocPct !== null && rocPct >= 0 ? '+' : '';
  const rocColor = rocPct !== null ? (rocPct >= 0 ? '#22c55e' : '#ef4444') : '#94a3b8';

  return (
    <div>
      <div style={styles.liveHeader}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={styles.livePrice}>{snapshot.current_price?.toFixed(2) ?? '—'}</span>
          {priceMovePct !== null && (
            <span style={{ fontSize: 13, color: moveColor, fontWeight: 600 }}>{moveSign}{priceMovePct.toFixed(1)}% ADR</span>
          )}
        </div>
        <button style={styles.refreshBtn} onClick={onRefresh}>↻ Refresh</button>
      </div>

      {/* Gap size row removed — now shown as separate "Gap Size" LevelRow below */}

      {/* Two-column layout: gap stats (left) | session stats (right) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        {/* LEFT: Gap stats */}
        <div>
          <LevelRow label="Gap Direction" value={gapDirLabel} color={gapDirColor} />
          <LevelRow label="Gap Size" value={`${gapSign}${snapshot.gap_points?.toFixed(2) ?? '—'} pts`} />
          <LevelRow label="Gap % ADR" value={adrPoints > 0 ? Math.abs((snapshot.gap_points / adrPoints) * 100).toFixed(1) + '% ADR' : '—'} />
          {gapFillPct !== null && (
            <LevelRow label="Gap Fill" value={`${gapFillPct.toFixed(0)}%`} color="#3b82f6" />
          )}
        </div>
        {/* RIGHT: Session stats */}
        <div>
          <LevelRow label="Session Open" value={snapshot.session_open} />
          <LevelRow label="Prev Close" value={snapshot.prev_close} />
          <LevelRow label="ROC" value={`${rocSign}${(rocPct ?? 0).toFixed(2)}%`} color={rocColor} />
          {openPosLabel && (
            <LevelRow label="Open Pos" value={openPosLabel.label} color={openPosLabel.color} />
          )}
        </div>
      </div>

      {hitStats.length > 0 && (
        <>
          <div style={{ ...styles.sectionLabel, marginTop: 12 }}>Levels Touched (% of similar days)</div>
          <div style={styles.levelGrid}>
            {hitStats.map(s => (
              <LevelRowWithPct key={s.label} label={s.label} value={s.value} pct={s.pct} />
            ))}
          </div>
        </>
      )}

      <div style={styles.updatedAt}>Updated: {new Date(snapshot.updated_at).toLocaleTimeString()}</div>
    </div>
  );
}

function LevelRow({ label, value, color = '#e2e8f0' }: { label: string; value: string | number; color?: string }) {
  const display = typeof value === 'number' ? value?.toFixed(2) ?? '—' : value;
  return (
    <div style={styles.levelRow}>
      <span style={styles.levelLabel}>{label}</span>
      <span style={{ ...styles.levelValue, color }}>{display}</span>
    </div>
  );
}

function LevelRowWithPct({ label, value, pct }: { label: string; value: number; pct: number }) {
  const color = pct >= 60 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div style={styles.levelRow}>
      <span style={styles.levelLabel}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ ...styles.levelValue, fontSize: 12 }}>{value?.toFixed(2) ?? '—'}</span>
        <span style={{ fontSize: 11, color, fontWeight: 700 }}>{pct}% ↗</span>
      </div>
    </div>
  );
}

function GapPanel({ stats }: { stats: StatItem[] }) {
  const byDir = stats.filter(s => s.filter.startsWith('dir:'));
  const byDow = stats.filter(s => s.filter.startsWith('dow:'));

  const dirColors: Record<string, string> = { up: '#22c55e', down: '#ef4444' };
  const dowNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div>
      <div style={styles.sectionLabel}>By Gap Direction</div>
      {byDir.map(s => {
        const dir = s.filter.replace('dir:', '');
        const d = s.data as { gapFillRate: number; bullishPct: number };
        return (
          <div key={s.filter} style={styles.statRow}>
            <span style={{ color: dirColors[dir] ?? '#888', fontWeight: 600, width: 50 }}>{dir?.toUpperCase()}</span>
            <div style={{ flex: 1 }}>
              <div style={styles.statRowLabel}>Fill Rate</div>
              {pctBar(d.gapFillRate, 100, dirColors[dir] ?? '#3b82f6')}
            </div>
            <div style={{ flex: 1 }}>
              <div style={styles.statRowLabel}>Bullish %</div>
              {pctBar(d.bullishPct, 100, dirColors[dir] ?? '#3b82f6')}
            </div>
          </div>
        );
      })}

      <div style={styles.dowGrid}>
        {byDow.map(s => {
          const dow = parseInt(s.filter.replace('dow:', ''));
          const d = s.data as { gapFillRate: number; bullishPct: number };
          return (
            <div key={s.filter} style={styles.dowCell}>
              <div style={styles.dowName}>{dowNames[dow]}</div>
              <div style={styles.dowPct}>Fill {d.gapFillRate?.toFixed(0)}%</div>
              <div style={{ ...styles.dowPct, color: d.bullishPct > 50 ? '#22c55e' : '#ef4444' }}>
                {d.bullishPct?.toFixed(0)}% ↗
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ORBPanel({ stats }: { stats: StatItem[] }) {
  const tfColors: Record<string, string> = { m5: '#f59e0b', m15: '#3b82f6', m30: '#8b5cf6', h1: '#ec4899' };

  return (
    <div>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.th}>
              <th>TF</th>
              <th>Break ↑</th>
              <th>Break ↓</th>
              <th>1R</th><th>2R</th><th>3R</th><th>4R</th><th>5R</th>
              <th>Avg↑</th><th>Avg↓</th>
            </tr>
          </thead>
          <tbody>
            {stats.map(s => {
              const d = s.data as Record<string, number>;
              const tf = (d as any).timeframe ?? s.filter.replace('tf:', '');
              return (
                <tr key={s.filter} style={styles.tr}>
                  <td style={{ color: tfColors[tf] ?? '#fff', fontWeight: 700 }}>{tf?.toUpperCase()}</td>
                  <td>{pctCell(d.breakUpPct)}</td>
                  <td>{pctCell(d.breakDownPct)}</td>
                  <td>{pctCell(d.hitRate1R)}</td>
                  <td>{pctCell(d.hitRate2R)}</td>
                  <td>{pctCell(d.hitRate3R)}</td>
                  <td>{pctCell(d.hitRate4R)}</td>
                  <td>{pctCell(d.hitRate5R)}</td>
                  <td>{d.avgExtensionUp?.toFixed(0)}</td>
                  <td>{d.avgExtensionDown?.toFixed(0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={styles.tableNote}>Break % = probability of IB breakout | 1R–5R = hit rate at N× the ORB range | Avg = avg extension pts beyond IB</div>
    </div>
  );
}

function pctCell(v?: number) {
  if (v == null) return <td>-</td>;
  const color = v >= 60 ? '#22c55e' : v >= 40 ? '#f59e0b' : '#ef4444';
  return <td style={{ color }}>{v.toFixed(1)}%</td>;
}

function SessionTypePanel({ stats }: { stats: StatItem[] }) {
  const typeColors: Record<string, string> = {
    range: '#f59e0b', trend_up: '#22c55e', trend_down: '#ef4444'
  };

  return (
    <div>
      {stats.map(s => {
        const d = s.data as { probability: number; avgRange: number };
        const type = s.filter.replace('type:', '');
        return (
          <div key={s.filter} style={styles.sessionTypeRow}>
            <span style={{ color: typeColors[type] ?? '#888', fontWeight: 700, textTransform: 'uppercase', width: 90 }}>{type?.replace('_', ' ')}</span>
            <div style={{ flex: 1 }}>{pctBar(d.probability, 100, typeColors[type] ?? '#3b82f6')}</div>
          </div>
        );
      })}
    </div>
  );
}

function TimingPanel({ stats }: { stats: StatItem[] }) {
  const hourLabels = ['00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23'];

  // Group by hour buckets
  const hours = Array.from({ length: 24 }, (_, i) => {
    const found = stats.find(s => (s.data as TimingData).hour === i);
    return found ? found.data as TimingData : { hour: i, highPct: 0, lowPct: 0 };
  });

  const maxVal = Math.max(...hours.map(h => Math.max(h.highPct, h.lowPct)), 1);

  return (
    <div>
      <div style={styles.timingChart}>
        {hours.map(h => (
          <div key={h.hour} style={styles.timingBarCol}>
            <div style={styles.timingBarWrap}>
              <div style={{ ...styles.timingBar, height: `${(h.highPct / maxVal) * 100}%`, background: '#22c55e' }} />
              <div style={{ ...styles.timingBar, height: `${(h.lowPct / maxVal) * 100}%`, background: '#ef4444' }} />
            </div>
            <div style={styles.timingHour}>{hourLabels[h.hour]}</div>
          </div>
        ))}
      </div>
      <div style={styles.timingLegend}>
        <span style={{ color: '#22c55e' }}>■ High</span>
        <span style={{ color: '#ef4444' }}>■ Low</span>
      </div>
    </div>
  );
}

function MultiDayPanel({ stats }: { stats: StatItem[] }) {
  const patternLabels: Record<string, string> = {
    up_then_gap_up: '↑ then Gap ↑',
    down_then_gap_down: '↓ then Gap ↓',
    up_then_gap_down: '↑ then Gap ↓',
    down_then_gap_up: '↓ then Gap ↑',
  };
  const patternColors: Record<string, string> = {
    up_then_gap_up: '#22c55e',
    down_then_gap_down: '#ef4444',
    up_then_gap_down: '#f59e0b',
    down_then_gap_up: '#8b5cf6',
  };

  return (
    <div>
      {stats.map(s => {
        const d = s.data as { directionalPct: number };
        const pattern = s.filter.replace('pattern:', '');
        return (
          <div key={s.filter} style={styles.sessionTypeRow}>
            <span style={{ color: patternColors[pattern] ?? '#888', fontWeight: 600, width: 140 }}>{patternLabels[pattern] ?? pattern}</span>
            <div style={{ flex: 1 }}>{pctBar(d.directionalPct, 100, patternColors[pattern] ?? '#3b82f6')}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function App() {
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [activeInst, setActiveInst] = useState<string>('DAX');
  const [activeView, setActiveView] = useState<'breakout' | 'currencies' | 'indices'>('breakout');
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);
  const [gapStats, setGapStats] = useState<StatItem[]>([]);
  const [orbStats, setOrbStats] = useState<StatItem[]>([]);
  const [sessionTypeStats, setSessionTypeStats] = useState<StatItem[]>([]);
  const [timingStats, setTimingStats] = useState<StatItem[]>([]);
  const [multiDayStats, setMultiDayStats] = useState<StatItem[]>([]);
  const [similarDays, setSimilarDays] = useState<SimilarDayData | null>(null);
  const [adrPoints, setAdrPoints] = useState<number>(0);
  const [levelProbs, setLevelProbs] = useState<LevelProbData>({});
  const [activeSubTab, setActiveSubTab] = useState<'orb' | 'level' | 'premkt'>('orb');
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [filterState, setFilterState] = useState<FilterState>({});
  const [filteredSampleSize, setFilteredSampleSize] = useState<number | null>(null);
  const [sessionContext, setSessionContext] = useState<SessionContextData | null>(null);

  const loadData = useCallback(async (inst: string, filters: FilterState) => {
    setLoading(true);
    try {
      const hasFilters = filters.dow !== undefined || filters.gapDir || filters.openPos ||
        filters.gapMin !== undefined || filters.gapMax !== undefined || filters.tf || filters.stopPct;

      if (hasFilters) {
        const [snap, filtered, simDays, lvProb, adrData] = await Promise.all([
          fetchSessionToday(inst),
          fetchFilteredStats(inst, filters),
          fetchSimilarDays(inst),
          fetchLevelProbabilities(inst),
          fetchAdr(inst),
        ]);
        setSnapshot(snap);
        setGapStats(filtered.gapStats);
        setOrbStats(filtered.orbStats);
        setSessionTypeStats(filtered.sessionTypeStats);
        setTimingStats(filtered.timingStats);
        setMultiDayStats(filtered.multiDayStats);
        setFilteredSampleSize(filtered.sampleSize);
        setSimilarDays(simDays);
        setLevelProbs(lvProb);
        setAdrPoints(adrData.adrPoints);
        setSessionContext(await fetchSessionContext(inst));
        setLastRefresh(new Date());
      } else {
        // No filters — use filtered-stats engine (still excludes Sundays)
        const [snap, filtered, simDays, lvProb, adrData] = await Promise.all([
          fetchSessionToday(inst),
          fetchFilteredStats(inst, {}),
          fetchSimilarDays(inst),
          fetchLevelProbabilities(inst),
          fetchAdr(inst),
        ]);
        setSnapshot(snap);
        setGapStats(filtered.gapStats);
        setOrbStats(filtered.orbStats);
        setSessionTypeStats(filtered.sessionTypeStats);
        setTimingStats(filtered.timingStats);
        setMultiDayStats(filtered.multiDayStats);
        setFilteredSampleSize(filtered.sampleSize);
        setSimilarDays(simDays);
        setLevelProbs(lvProb);
        setAdrPoints(adrData.adrPoints);
        setSessionContext(await fetchSessionContext(inst));
        setLastRefresh(new Date());
      }
    } catch (e) {
      console.error('Failed to load data', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstruments().then(insts => {
      setInstruments(insts);
      if (insts.length > 0) loadData(insts[0].key, filterState);
    }).catch(console.error);
  }, [loadData]);

  useEffect(() => {
    if (!activeInst) return;
    loadData(activeInst, filterState);
    const interval = setInterval(() => loadData(activeInst, filterState), 60_000);
    return () => clearInterval(interval);
  }, [activeInst, filterState, loadData]);

  const handleRefresh = async () => {
    if (!activeInst) return;
    await refreshSession(activeInst);
    await loadData(activeInst, filterState);
  };

  function SessionRangeBadge(props: { sessionContext: SessionContextData | null }) {
    const sc = props.sessionContext;
    if (!sc || sc.sessionBar <= 0) return null;
    if (sc.bar18Alert) {
      return (
        <div style={{ background: '#f59e0b1a', border: '1px solid #f59e0b55', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#f59e0b', marginBottom: 12 }}>
          ⚠️ Bar {sc.sessionBar}+ — Range {sc.sessionRangePct.toFixed(0)}% of ASR. New extremes less likely.
        </div>
      );
    }
    if (sc.sessionBar >= 14) {
      return (
        <div style={{ background: '#3b82f61a', border: '1px solid #3b82f633', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
          📊 Bar {sc.sessionBar} — Range {sc.sessionRangePct.toFixed(0)}% of ASR
        </div>
      );
    }
    return null;
  }

  const handleFilterChange = (f: FilterState) => {
    setFilterState(f);
  };

  const handleApplySimilarGap = (gapDir: 'up' | 'down', gapMin: number, gapMax: number) => {
    setFilterState(prev => ({ ...prev, gapDir, gapMin, gapMax }));
  };

  return (
    <div style={styles.root}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.siteName}>ToshioTradeStats</span>
          <div style={styles.poweredBy}>
            <img src="/ftmo_logo_header.png" alt="FTMO" style={styles.dkLogo} />
            <span style={styles.poweredText}>Powered by FTMO</span>
          </div>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.lastRefresh}>Last update: {lastRefresh.toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Instrument Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeView === 'breakout' ? styles.tabActive : {}) }}
          onClick={() => setActiveView('breakout')}
        >
          📈 Breakout Scan
        </button>
        <button
          style={{ ...styles.tab, ...(activeView === 'currencies' ? styles.tabActive : {}) }}
          onClick={() => setActiveView('currencies')}
        >
          💱 Currencies
        </button>
        <button
          style={{ ...styles.tab, ...(activeView === 'indices' ? styles.tabActive : {}) }}
          onClick={() => setActiveView('indices')}
        >
          📊 Indices
        </button>
      </div>

      {activeView === 'breakout' && (
        <div style={styles.content}>
          <BreakoutScanPanel />
        </div>
      )}

      {activeView === 'currencies' && (
        <div style={styles.content}>
          <CurrenciesPanel />
        </div>
      )}

      {activeView === 'indices' && (
      <>
      {/* Index Instrument Sub-Tabs */}
      <div style={{ ...styles.tabs, paddingTop: 0 }}>
        {instruments.map(inst => (
          <button
            key={inst.key}
            style={{ ...styles.tab, ...(activeInst === inst.key ? styles.tabActive : {}) }}
            onClick={() => setActiveInst(inst.key)}
          >
            {inst.key}
          </button>
        ))}
      </div>

      {/* Main content */}
        <div style={styles.content}>
        {loading ? loadingSpinner() : (
          <>
            <div style={styles.grid}>
              {/* Live Session */}
              {(() => {
                const marketOpen = isMarketOpen(activeInst);
                const gapDir = snapshot && snapshot.gap_points > 0 ? 'up' : snapshot && snapshot.gap_points < 0 ? 'down' : 'flat';
                const gapFillPct = gapDir ? (gapStats.find(s => s.filter === `dir:${gapDir}`) as { data: { gapFillRate: number } } | undefined)?.data?.gapFillRate ?? null : null;
                const header = marketOpen ? '🔴 Live Session' : '⚪ Market Closed';
                const accentColor = marketOpen ? '#ef4444' : '#6b7280';
                return card(header, <LiveSessionPanel snapshot={snapshot} similarDays={similarDays} adrPoints={adrPoints} gapFillPct={gapFillPct} onRefresh={handleRefresh} instrument={activeInst} />, accentColor);
              })()}
              {card('📊 TradingView Chart', <TradingViewChart instrument={activeInst} snapshot={snapshot} />, '#8b5cf6')}
              {card('⚡ Gap Edge', <SimilarDaysPanel instrument={activeInst} onApplySimilarGap={(dir, min, max) => {
                handleApplySimilarGap(dir, min, max);
                setActiveSubTab('orb');
              }} />, '#f59e0b')}

              {/* PROBABILITIES SECTION */}
              <div style={{ gridColumn: '1 / -1' }}>
                <EdgeSummaryPanel
                  instrument={activeInst}
                  snapshot={snapshot}
                  similarDays={similarDays}
                  adrPoints={adrPoints}
                  sessionContext={sessionContext}
                />
                <SessionRangeBadge sessionContext={sessionContext} />
                <FilterPanel filters={filterState} onChange={handleFilterChange} sessionCount={filteredSampleSize ?? undefined} />
                <div style={styles.probTabs}>
                  <button style={{ ...styles.probTab, ...(activeSubTab === 'orb' ? styles.probTabActive : {}) }} onClick={() => setActiveSubTab('orb')}>ORB Stats</button>
                  <button style={{ ...styles.probTab, ...(activeSubTab === 'level' ? styles.probTabActive : {}) }} onClick={() => setActiveSubTab('level')}>Level Prob</button>
                  {['US30', 'NASDAQ', 'SP500'].includes(activeInst) && (
                    <button style={{ ...styles.probTab, ...(activeSubTab === 'premkt' ? styles.probTabActive : {}) }} onClick={() => setActiveSubTab('premkt')}>Pre-Market</button>
                  )}
                  {filteredSampleSize !== null && (
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#3b82f6', alignSelf: 'center', fontWeight: 600 }}>
                      Filtered: {filteredSampleSize.toLocaleString()} sessions
                      {(() => {
                        const parts: string[] = [];
                        if (filterState.dow !== undefined) {
                          const dowNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                          parts.push(dowNames[filterState.dow]);
                        }
                        if (filterState.gapDir) parts.push(filterState.gapDir === 'up' ? 'Gap Up' : 'Gap Down');
                        if (filterState.openPos) parts.push(filterState.openPos === 'inside' ? 'Inside' : filterState.openPos === 'above' ? 'Above' : 'Below');
                        return parts.length > 0 ? ` (${parts.join(' + ')})` : null;
                      })()}
                    </span>
                  )}
                </div>
                
                {activeSubTab === 'level' && <LevelProbabilitiesPanel instrument={activeInst} snapshot={snapshot} />}
                {activeSubTab === 'premkt' && ['US30', 'NASDAQ', 'SP500'].includes(activeInst) && <PreMarketPanel instrument={activeInst} />}
                {activeSubTab === 'orb' && (
                  <>
                    <ORBFilterPanel filters={filterState} onChange={handleFilterChange} />
                    <ORBDashboard stats={orbStats} />
                  </>
                )}
              </div>
            </div>
          </>
        )}
        </div>
      </>
      )}

      <div style={styles.footer}>📈 ToshioTradeStats · Data from FTMO · Auto-refreshes every 60s</div>
    </div>
  );
}

// ---- Styles ----

const styles: Record<string, React.CSSProperties> = {
  root: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background: '#090b14',
    color: '#e8e6e0',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 36px',
    borderBottom: '1px solid rgba(245,158,11,0.08)',
    background: 'rgba(9,11,20,0.92)',
    backdropFilter: 'blur(20px)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 1px 0 rgba(245,158,11,0.05)',
  },
  logo: {
    fontSize: 20,
    fontWeight: 700,
    color: '#f8fafc',
    letterSpacing: '-0.5px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    paddingLeft: 8,
  },
  siteName: {
    fontSize: 18,
    fontWeight: 800,
    color: '#f5f3ef',
    letterSpacing: '-0.4px',
    background: 'linear-gradient(90deg, #e8e6e0 30%, #a5b4fc 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  poweredBy: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 3,
  },
  dkLogo: {
    height: 26,
    width: 'auto',
    objectFit: 'contain',
    filter: 'brightness(1.1) contrast(1.05)',
  },
  poweredText: {
    fontSize: 11,
    color: '#4a5568',
    fontWeight: 500,
    letterSpacing: '0.04em',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 28,
  },
  lastRefresh: {
    fontSize: 11,
    color: '#3a4055',
    fontFamily: "'JetBrains Mono', monospace",
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    padding: '10px 36px',
    background: 'rgba(9,11,20,0.6)',
    borderBottom: '1px solid rgba(245,158,11,0.06)',
    overflowX: 'auto',
  },
  tab: {
    padding: '8px 20px',
    borderRadius: 10,
    border: '1px solid transparent',
    background: 'transparent',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.18s',
    letterSpacing: '0.02em',
  },
  tabActive: {
    background: 'rgba(245,158,11,0.10)',
    border: '1px solid rgba(245,158,11,0.25)',
    color: '#f59e0b',
    boxShadow: '0 0 14px rgba(245,158,11,0.10)',
  },
  content: {
    flex: 1,
    padding: '28px 36px',
    overflowY: 'auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320, 1fr))',
    gap: 16,
  },
  card: {
    background: 'rgba(22,24,42,0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: 16,
    padding: '22px 24px',
    border: '1px solid rgba(245,158,11,0.12)',
    boxShadow: '0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.03)',
  },
  cardTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    color: '#4a5568',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    marginBottom: 16,
  },
  loading: {
    padding: 28,
    textAlign: 'center',
    color: '#3a4055',
    fontSize: 13,
  },
  liveHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  livePrice: {
    fontSize: 36,
    fontWeight: 800,
    color: '#f5f3ef',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    letterSpacing: '-2px',
  },
  refreshBtn: {
    padding: '8px 18px',
    borderRadius: 10,
    border: '1px solid rgba(245,158,11,0.3)',
    background: 'rgba(245,158,11,0.08)',
    color: '#f59e0b',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.18s',
    boxShadow: '0 0 12px rgba(245,158,11,0.08)',
  },
  gapRow: {
    marginBottom: 12,
  },
  levelGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
  },
  levelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '7px 0',
    borderBottom: '1px solid rgba(41,52,255,0.08)',
  },
  levelLabel: {
    fontSize: 12,
    color: '#4a5568',
    fontWeight: 500,
  },
  levelValue: {
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "'JetBrains Mono', monospace",
    color: '#c8c4bc',
  },
  updatedAt: {
    fontSize: 11,
    color: '#475569',
    marginTop: 8,
    textAlign: 'right',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 8,
    marginTop: 12,
  },
  statRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  statRowLabel: {
    fontSize: 11,
    color: '#64748b',
    marginBottom: 3,
  },
  sample: {
    fontSize: 11,
    color: '#475569',
    width: 60,
    textAlign: 'right',
  },
  pctBarWrap: {
    position: 'relative',
    height: 20,
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 100,
    overflow: 'hidden',
    marginBottom: 2,
  },
  pctBarFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    borderRadius: 100,
    transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
    boxShadow: '0 0 10px rgba(245,158,11,0.25)',
  },
  pctBarLabel: {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 10,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.5)',
    fontFamily: "'JetBrains Mono', monospace",
  },
  dowGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 8,
  },
  dowCell: {
    background: 'rgba(18,20,38,0.6)',
    borderRadius: 12,
    padding: '12px 6px',
    textAlign: 'center',
    border: '1px solid rgba(41,52,255,0.08)',
  },
  dowName: {
    fontSize: 11,
    fontWeight: 700,
    color: '#4a5568',
    marginBottom: 6,
    letterSpacing: '0.04em',
  },
  dowPct: {
    fontSize: 11,
    color: '#4a5568',
    marginTop: 2,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  },
  th: {
    textAlign: 'left',
    color: '#64748b',
    fontWeight: 600,
    fontSize: 11,
    padding: '4px 8px',
    borderBottom: '1px solid #334155',
  },
  tr: {
    borderBottom: '1px solid #1e293b',
  },
  tableNote: {
    fontSize: 11,
    color: '#475569',
    marginTop: 8,
    lineHeight: 1.5,
  },
  sessionTypeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  timingChart: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 2,
    height: 100,
    overflowX: 'auto',
  },
  timingBarCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    minWidth: 8,
  },
  timingBarWrap: {
    display: 'flex',
    gap: 1,
    alignItems: 'flex-end',
    height: 80,
  },
  timingBar: {
    width: 4,
    borderRadius: '2px 2px 0 0',
    minHeight: 2,
  },
  timingHour: {
    fontSize: 9,
    color: '#475569',
    marginTop: 4,
    fontFamily: 'monospace',
  },
  timingLegend: {
    display: 'flex',
    gap: 16,
    marginTop: 8,
    fontSize: 12,
    justifyContent: 'center',
  },
  footer: {
    textAlign: 'center',
    padding: '16px',
    fontSize: 11,
    color: '#2a2f45',
    borderTop: '1px solid rgba(41,52,255,0.06)',
    letterSpacing: '0.02em',
  },
  probTabs: {
    display: 'flex',
    gap: 4,
    marginBottom: 12,
  },
  probTab: {
    padding: '6px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#1e293b',
    color: '#6b7280',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  probTabActive: {
    background: '#f59e0b',
    color: '#0a0b12',
  },
};

// Error boundary
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {error: Error | null; componentStack: string | null}> {
  state = { error: null as Error | null, componentStack: null as string | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
  }
  render() {
    if (this.state.error) {
      return <div style={{background:'#1e1e2e',color:'#ff6b6b',padding:40,fontFamily:'monospace',whiteSpace:'pre-wrap',fontSize:11}}>
        <h2>💹 ToshioTradeStats Error</h2>
        <p>{this.state.error.message}</p>
        <pre style={{fontSize:10}}>{this.state.componentStack ?? this.state.error.stack}</pre>
      </div>;
    }
    return this.props.children;
  }
}

// Mount the app
import { createRoot } from 'react-dom/client';
const rootEl = document.getElementById('root');
if (rootEl) {
  try {
    const root = createRoot(rootEl);
    root.render(<ErrorBoundary><App /></ErrorBoundary>);
  } catch (e: any) {
    rootEl.innerHTML = `<div style="background:#1e1e2e;color:#ff6b6b;padding:40px;font-family:monospace"><h2>Mount Error</h2><pre>${e.message}\n${e.stack}</pre></div>`;
  }
} else {
  document.body.innerHTML = '<div style="color:red;padding:40px">No #root element found</div>';
}

